import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
} from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { formatDateTimeRu } from "../utils/formatDate";
import {
  ACHIEVEMENTS,
  xpToLevel,
} from "../utils/gamification";
import {
  gamificationEndpoints,
  learnerAccuracy,
  learnerDisplayName,
  rankBadge,
} from "../utils/gamificationApi";
import { setAuthToken } from "../utils/tokenHeaders";
import { readApiJson } from "../utils/apiBase";
import {
  DEMO_LEARNER_ID,
  getDemoLearnerProfile,
  getRatingDemoView,
  isDemoLearnerId,
} from "../utils/ratingDemoData";
import "./team-motivation.css";

function StatCard({ icon, label, value, hint }) {
  return (
    <article className="motivation-stat">
      <div className="motivation-stat__icon">{icon}</div>
      <div className="motivation-stat__body">
        <span className="motivation-stat__label">{label}</span>
        <strong className="motivation-stat__value">{value}</strong>
        {hint ? <span className="motivation-stat__hint">{hint}</span> : null}
      </div>
    </article>
  );
}

function PodiumCard({ row }) {
  if (!row) return null;
  return (
    <article className={`motivation-podium motivation-podium--place-${row.rank}`}>
      <span className="motivation-podium__medal">{rankBadge(row.rank)}</span>
      <strong className="motivation-podium__name">{learnerDisplayName(row)}</strong>
      <span className="motivation-podium__xp">{row.xp ?? 0} XP</span>
      <span className="motivation-podium__meta">
        Ур. {row.level ?? xpToLevel(row.xp)} · {learnerAccuracy(row)}
      </span>
    </article>
  );
}

export default function TeamMotivationHub({ scope = "team", gameId = "" }) {
  const endpoints = useMemo(
    () => gamificationEndpoints(scope, gameId),
    [scope, gameId]
  );

  const [data, setData] = useState(null);
  const [learners, setLearners] = useState([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    if (scope !== "team" && !gameId) return;

    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      fetch(endpoints.overview, { headers: { Authorization: setAuthToken() } }),
      fetch(endpoints.learners, { headers: { Authorization: setAuthToken() } }),
    ])
      .then(async ([overviewRes, learnersRes]) => {
        if (!overviewRes.ok) {
          const payload = await readApiJson(overviewRes).catch(() => ({}));
          if (overviewRes.status === 401) {
            throw new Error("Требуется авторизация. Войдите в аккаунт заново.");
          }
          if (overviewRes.status === 404) {
            throw new Error(
              "Раздел рейтинга недоступен на сервере. Перезапустите сервер или дождитесь обновления."
            );
          }
          throw new Error(payload.message || "Не удалось загрузить данные рейтинга");
        }
        const overview = await readApiJson(overviewRes);
        let learnersList = overview.leaderboard ?? [];
        if (learnersRes.ok) {
          const parsed = await readApiJson(learnersRes);
          learnersList = parsed.learners ?? learnersList;
        }
        if (!cancelled) {
          if (!learnersList.length) {
            const demo = getRatingDemoView();
            setIsDemo(true);
            setData(demo.data);
            setLearners(demo.learners);
            setSelectedLearnerId(demo.defaultLearnerId);
            setProfile(getDemoLearnerProfile(DEMO_LEARNER_ID));
            return;
          }

          setIsDemo(false);
          setData(overview);
          setLearners(learnersList);
          if (learnersList[0]?.messengerId) {
            setSelectedLearnerId(String(learnersList[0].messengerId));
          }
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError.message || "Ошибка загрузки");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [endpoints, scope, gameId]);

  useEffect(() => {
    if (!selectedLearnerId) {
      setProfile(null);
      return;
    }
    if (scope !== "team" && !gameId) return;

    if (isDemo && isDemoLearnerId(selectedLearnerId)) {
      setProfile(getDemoLearnerProfile(selectedLearnerId));
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);

    fetch(endpoints.learner(selectedLearnerId), {
      headers: { Authorization: setAuthToken() },
    })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Не удалось загрузить профиль");
        }
        return response.json();
      })
      .then((payload) => {
        if (!cancelled) setProfile(payload);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [endpoints, selectedLearnerId, scope, gameId, isDemo]);

  const leaderboard = data?.leaderboard ?? learners;
  const monthly = data?.monthlyChallenge;
  const stats = data?.achievementStats ?? {};
  const podium = leaderboard.slice(0, 3);
  const selectedLearner = learners.find(
    (item) => String(item.messengerId) === String(selectedLearnerId)
  );

  if (loading) {
    return (
      <div className="motivation-loading">
        <CircularProgress size={28} />
        <span>Загружаем рейтинг…</span>
      </div>
    );
  }

  if (error) {
    return <div className="motivation-empty motivation-empty--error">{error}</div>;
  }

  if (!leaderboard.length) {
    return (
      <div className="motivation-empty">
        <EmojiEventsOutlinedIcon />
        <strong>Пока нет активности</strong>
        <p>
          Когда сотрудники начнут проходить сценарии и тренажёры, здесь появятся
          рейтинг, достижения и профили.
        </p>
      </div>
    );
  }

  return (
    <div className="motivation-hub">
      {isDemo ? (
        <div className="motivation-demo-banner" role="status">
          <span className="motivation-demo-banner__badge">Пример</span>
          <p>
            Показаны демонстрационные данные. Когда сотрудники начнут проходить
            обучение, здесь появятся их реальные результаты, XP и достижения.
          </p>
        </div>
      ) : null}
      <section className="motivation-kpi">
        <StatCard
          icon={<TrendingUpOutlinedIcon />}
          label="Участников"
          value={data?.totals?.learners ?? leaderboard.length}
          hint="сотрудников в рейтинге"
        />
        <StatCard
          icon={<EmojiEventsOutlinedIcon />}
          label="Завершений"
          value={data?.totals?.completions ?? 0}
          hint="по всем проектам"
        />
        <StatCard
          icon={<MilitaryTechOutlinedIcon />}
          label="Лидер"
          value={leaderboard[0] ? learnerDisplayName(leaderboard[0]) : "—"}
          hint={leaderboard[0] ? `${leaderboard[0].xp ?? 0} XP` : ""}
        />
        <StatCard
          icon={<LocalFireDepartmentOutlinedIcon />}
          label="Челлендж"
          value={
            monthly
              ? `${monthly.leaders?.filter((item) => item.reachedTarget).length ?? 0} / ${monthly.targetCompletions}`
              : "—"
          }
          hint={monthly ? `месяц ${monthly.month}` : "не включён"}
        />
      </section>

      <div className="motivation-layout">
        <div className="motivation-layout__main">
          <section className="motivation-card">
            <header className="motivation-card__head">
              <div>
                <h2>Рейтинг команды</h2>
                <p>Результаты по всем сценариям, тренажёрам и базам знаний</p>
              </div>
            </header>

            <div className="motivation-podium-row">
              <PodiumCard row={podium[1]} />
              <PodiumCard row={podium[0]} />
              <PodiumCard row={podium[2]} />
            </div>

            <div className="motivation-table-wrap">
              <table className="motivation-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Сотрудник</th>
                    <th>XP</th>
                    <th>Уровень</th>
                    <th>Завершений</th>
                    <th>Точность</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((row) => (
                    <tr
                      key={row.messengerId}
                      className={
                        String(selectedLearnerId) === String(row.messengerId)
                          ? "is-active"
                          : ""
                      }
                      onClick={() =>
                        setSelectedLearnerId(String(row.messengerId))
                      }
                    >
                      <td>{rankBadge(row.rank)}</td>
                      <td>
                        <span className="motivation-table__name">
                          {learnerDisplayName(row)}
                        </span>
                        {row.username ? (
                          <span className="motivation-table__username">
                            @{row.username.replace(/^@/, "")}
                          </span>
                        ) : null}
                      </td>
                      <td>{row.xp ?? 0}</td>
                      <td>{row.level ?? xpToLevel(row.xp)}</td>
                      <td>{row.totalCompletions ?? 0}</td>
                      <td>{learnerAccuracy(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="motivation-card">
            <header className="motivation-card__head">
              <div>
                <h2>Достижения команды</h2>
                <p>Сколько сотрудников получили каждый бейдж</p>
              </div>
            </header>
            <div className="motivation-team-achievements">
              {ACHIEVEMENTS.map((item) => (
                <article key={item.id} className="motivation-team-achievement">
                  <span className="motivation-team-achievement__icon">
                    {item.icon}
                  </span>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.description}</p>
                  </div>
                  <span className="motivation-team-achievement__count">
                    {stats[item.id] ?? 0}
                  </span>
                </article>
              ))}
            </div>
          </section>

          {monthly?.leaders?.length ? (
            <section className="motivation-card">
              <header className="motivation-card__head">
                <div>
                  <h2>Челлендж месяца</h2>
                  <p>
                    Цель: {monthly.targetCompletions} завершений · {monthly.month}
                  </p>
                </div>
              </header>
              <div className="motivation-table-wrap">
                <table className="motivation-table">
                  <thead>
                    <tr>
                      <th>Сотрудник</th>
                      <th>Завершений</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthly.leaders.slice(0, 10).map((row) => {
                      const learner = learners.find(
                        (item) => Number(item.messengerId) === Number(row.messengerId)
                      );
                      return (
                        <tr key={row.messengerId}>
                          <td>
                            {learner
                              ? learnerDisplayName(learner)
                              : row.messengerId}
                          </td>
                          <td>{row.completions}</td>
                          <td>
                            <span
                              className={`motivation-badge${
                                row.reachedTarget
                                  ? " motivation-badge--success"
                                  : ""
                              }`}
                            >
                              {row.reachedTarget ? "Цель достигнута" : "В процессе"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </div>

        <aside className="motivation-layout__aside">
          <section className="motivation-card motivation-card--sticky">
            <header className="motivation-card__head">
              <div>
                <h2>Профиль сотрудника</h2>
                <p>Достижения и история прохождений</p>
              </div>
            </header>

            <Autocomplete
              options={learners}
              value={selectedLearner ?? null}
              onChange={(_, value) =>
                setSelectedLearnerId(value ? String(value.messengerId) : "")
              }
              getOptionLabel={(option) =>
                option.rank
                  ? `${learnerDisplayName(option)} · #${option.rank}`
                  : learnerDisplayName(option)
              }
              isOptionEqualToValue={(option, value) =>
                String(option.messengerId) === String(value.messengerId)
              }
              renderInput={(params) => (
                <TextField {...params} label="Выберите сотрудника" size="small" />
              )}
            />

            {profileLoading ? (
              <div className="motivation-loading motivation-loading--inline">
                <CircularProgress size={22} />
              </div>
            ) : null}

            {!profileLoading && profile ? (
              <div className="motivation-profile">
                <div className="motivation-profile__hero">
                  <span className="motivation-profile__rank">
                    {profile.learner.rank
                      ? rankBadge(profile.learner.rank)
                      : "—"}
                  </span>
                  <div>
                    <h3>{profile.learner.displayName}</h3>
                    {profile.learner.username ? (
                      <p>@{profile.learner.username.replace(/^@/, "")}</p>
                    ) : null}
                  </div>
                </div>

                <div className="motivation-profile__metrics">
                  <div>
                    <span>XP</span>
                    <strong>{profile.learner.xp ?? 0}</strong>
                  </div>
                  <div>
                    <span>Уровень</span>
                    <strong>
                      {profile.learner.level ?? xpToLevel(profile.learner.xp)}
                    </strong>
                  </div>
                  <div>
                    <span>Серия</span>
                    <strong>{profile.learner.currentStreak ?? 0} дн.</strong>
                  </div>
                  <div>
                    <span>Завершений</span>
                    <strong>{profile.learner.totalCompletions ?? 0}</strong>
                  </div>
                </div>

                <div className="motivation-profile__achievements">
                  <h4>Достижения</h4>
                  <div className="motivation-achievements-grid">
                    {profile.achievements.map((item) => (
                      <article
                        key={item.id}
                        className={`motivation-achievement${
                          item.unlocked ? " is-unlocked" : ""
                        }`}
                      >
                        <span>{item.icon}</span>
                        <div>
                          <strong>{item.title}</strong>
                          <small>
                            {item.unlocked
                              ? formatDateTimeRu(item.unlockedAt)
                              : "Не получено"}
                          </small>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>

                <div className="motivation-profile__history">
                  <h4>История прохождений</h4>
                  {profile.sessions.length === 0 ? (
                    <p className="motivation-empty-inline">Сессий пока нет.</p>
                  ) : (
                    <ul className="motivation-session-list">
                      {profile.sessions.map((session) => (
                        <li key={session.sessionId} className="motivation-session">
                          <div className="motivation-session__top">
                            <strong>{session.projectTitle}</strong>
                            <span className="motivation-badge">
                              {session.contentTypeLabel}
                            </span>
                          </div>
                          <p className="motivation-session__result">
                            {session.resultLabel}
                          </p>
                          <p className="motivation-session__dates">
                            {formatDateTimeRu(session.startedAt)}
                            {session.completed
                              ? ` → ${formatDateTimeRu(session.completedAt)}`
                              : ""}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : null}
          </section>
        </aside>
      </div>
    </div>
  );
}
